/**
 * The init job's report of one environment (`initReport.ts`), read ONCE for
 * the branch page's overview and handed to both its readers: the « Modules »
 * card (`InitReportCard.tsx`) and the banner at the top of the tab, which
 * says Odoo is unavailable while the job runs. Two components fetching it
 * on their own would make two requests per tick and could disagree for up
 * to a tick on whether a run is in progress.
 *
 * POLLING. While the last answer is a run in progress
 * (`initReportPhase` === 'running'), the route is read again every
 * {@link INIT_REPORT_POLL_MS}; it stops on a finished report, on none, on
 * a running one past `INIT_REPORT_STALE_MS` (interrupted: no write will
 * come), and on unmount. One read at a time: the next is scheduled once the
 * previous has answered. Paused while the document is hidden (another
 * browser tab, a locked phone) — nobody is looking, and every open page
 * polling would load the backend for nothing — and resumed with an
 * immediate read when it is shown again. The backend keeps a namespace's
 * answer 15 s (`INIT_REPORT_TTL_MS`, initReportRoute.ts): a run's end is
 * seen within ~25 s.
 *
 * FAILURES. A failed first read hides the card, the rule it always had
 * (nothing the reader can act on); a failed read while polling keeps the
 * last report on screen, and the next tick tries again.
 *
 * Keyed by project and namespace: a caller passing another environment
 * without being remounted must not show dev-a's report on dev-b while
 * dev-b's is read. `generation` is the page's load counter: Refresh reads
 * again, keeping the report on screen meanwhile. `refreshKey` reads again
 * too — the page learnt of a new deployment, which may have started a new
 * init job — and a failure then keeps the last report, as while polling.
 */

import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  fetchInitReport,
  initReportPhase,
  type InitReport,
} from './initReport';

/** How often a run in progress is read again. */
export const INIT_REPORT_POLL_MS = 10_000;

export interface UseInitReportOptions {
  /** The project of the page's route — the backend authorizes on it. */
  project: string;
  /** The environment's namespace; nothing is read without one. */
  namespace: string | undefined;
  /** The page's load counter: a new value reads again (Refresh). */
  generation: number;
  /**
   * A new value reads again, as a poll would: a failure keeps the report
   * shown. Undefined by default.
   */
  refreshKey?: number;
  /**
   * False until the overview has been opened: like the cards of every tab,
   * nothing is read for a tab nobody looked at (BranchPage's lazy mounting).
   */
  enabled: boolean;
}

interface Answer {
  /** `<project>/<namespace>` the report is for. */
  key: string;
  report: InitReport | null;
  /** When it was received: a new object per read, so every tick renders. */
  readAt: number;
}

/** The environment's report; null without one, before it, or on a failure. */
export function useInitReport(
  options: UseInitReportOptions,
): InitReport | null {
  const { project, namespace, generation, enabled, refreshKey } = options;
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [answer, setAnswer] = useState<Answer>();
  const key = `${project}/${namespace ?? ''}`;
  // What the last run of the effect below read for: a run started by
  // `refreshKey` alone is no first read.
  const lastRun = useRef<{ key: string; generation: number }>();

  useEffect(() => {
    if (!enabled || !namespace) {
      return undefined;
    }
    const answerKey = `${project}/${namespace}`;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    // Whether the last answer was a run in progress: another read is due.
    let polling = false;
    let first =
      lastRun.current?.key !== answerKey ||
      lastRun.current.generation !== generation;
    lastRun.current = { key: answerKey, generation };

    const load = async () => {
      inFlight = true;
      try {
        const report = await fetchInitReport({
          fetchApi,
          discoveryApi,
          project,
          namespace,
        });
        if (!active) {
          return;
        }
        setAnswer({ key: answerKey, report, readAt: Date.now() });
        polling =
          report !== null && initReportPhase(report, Date.now()) === 'running';
      } catch {
        if (!active) {
          return;
        }
        // Hidden on a failed first read (the card's rule); kept while
        // polling, the next tick trying again (`polling` unchanged).
        if (first) {
          setAnswer({ key: answerKey, report: null, readAt: Date.now() });
        }
      } finally {
        inFlight = false;
        first = false;
      }
      // The next read, while a run is in progress and someone can see it.
      if (active && polling && !document.hidden && timer === undefined) {
        timer = setTimeout(() => {
          timer = undefined;
          void load();
        }, INIT_REPORT_POLL_MS);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
        timer = undefined;
      } else if (polling && timer === undefined && !inFlight) {
        void load();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    project,
    namespace,
    generation,
    refreshKey,
    enabled,
    fetchApi,
    discoveryApi,
  ]);

  return answer?.key === key ? answer.report : null;
}
